import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import React from 'react';

import { DomainError } from 'domain-errors';

import { useDebugVisibleContext } from '../contexts/DebugVisibleContext';
import isWebauthnTransientError from '../util/isWebauthnTransientError';

import ActionButton from './actionButton/ActionButton';
import ExternalLink from './externalLink/ExternalLink';
import styles from './FallbackComponent.module.css';
import HeyloginLogo from './HeyloginLogo';

export default function FallbackComponent({
  error,
  hideReload = false,
}: {
  error: unknown;
  hideReload?: boolean;
}) {
  const debugVisible = useDebugVisibleContext();

  return (
    <div className={styles.Content}>
      <h1 className={styles.Logo}>
        <HeyloginLogo />
      </h1>
      {error instanceof DomainError || isErrorObject(error) ?
        <>
          <h2 className={styles.Headline}>{error.userTitle}</h2>
          <p>{error.userDetail}</p>
        </>
      : isDynamicImportError(error) ?
        <>
          <h2 className={styles.Headline}>
            <Trans>heylogin was updated</Trans>
          </h2>
          <p>
            <Trans>Please reload to use the latest version.</Trans>
          </p>
        </>
      : isNotFoundError(error) ?
        <>
          <h2 className={styles.Headline}>
            <Trans>Page modified unexpectedly</Trans>
          </h2>
          <p>
            <Trans>
              An error happened because the page was modified by an extension or translation tool.
              Please disable translation or extensions and try again.
            </Trans>
          </p>
        </>
      : isWebauthnTransientError(error) ?
        <>
          <h2 className={styles.Headline}>
            <Trans>Security key access not allowed</Trans>
          </h2>
          <p>
            <Trans>
              Your browser was unable to access the security key. You may need to grant permissions
              in your operating system.
            </Trans>{' '}
            <ExternalLink
              href={t({
                message:
                  'https://help.heylogin.com/en/e7d73dc2d84c4306ad83129ae969b4d7#29c8872c7eda80ceb294e0ff292f7b51',
                comment: 'help center link: security keys / faq / firefox on windows error',
              })}
            >
              <Trans>Learn more</Trans>
            </ExternalLink>
          </p>
        </>
      : error instanceof Error ?
        <>
          <h2 className={styles.Headline}>
            <Trans>Unknown error: {error?.name}</Trans>
          </h2>
          <p>{error?.message}</p>
        </>
      : <>
          <h2 className={styles.Headline}>
            <Trans>Unknown error</Trans>
          </h2>
          <p>{JSON.stringify(error)}</p>
        </>
      }
      {!hideReload && (
        <ActionButton buttonStyle="primary" type="button" onClick={() => window.location.reload()}>
          <Trans>Reload</Trans>
        </ActionButton>
      )}
      {debugVisible && (
        <div style={{ border: '5px solid #f0f', marginTop: '2rem', padding: '.5rem' }}>
          <button
            onClick={() => {
              localStorage.clear();
              return window.location.reload();
            }}
          >
            Clear local storage and reload
          </button>
        </div>
      )}
    </div>
  );
}

export const DYNAMIC_IMPORT_ERROR_PREFIXES = [
  'Unable to preload CSS for',
  // Firefox
  'error loading dynamically imported module:',
  // Chrome
  'Failed to fetch dynamically imported module:',
  // Safari
  'Importing a module script failed',
];

function isDynamicImportError(error: unknown) {
  return (
    error instanceof Error &&
    DYNAMIC_IMPORT_ERROR_PREFIXES.some((prefix) => error.message.startsWith(prefix))
  );
}

function isNotFoundError(error: unknown) {
  return error instanceof DOMException && error.name === 'NotFoundError';
}

function isErrorObject(error: unknown): error is { userTitle: string; userDetail: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'userTitle' in error &&
    'userDetail' in error &&
    typeof error.userTitle === 'string' &&
    typeof error.userDetail === 'string'
  );
}
